import { LoggerProvider, BatchLogRecordProcessor } from '@opentelemetry/sdk-logs'
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions'
import { logs, SeverityNumber, Logger, type LogRecord } from '@opentelemetry/api-logs'
import { buildOtelAttributesClient } from './otel-attributes-client'
import { redactPresignedUrls } from './span-attribute-scrubber'

let loggerProvider: LoggerProvider | null = null
let logger: Logger | null = null
/**
 * Only the levels we actually override are captured. W1/D9: browser console
 * logs are NOT sampled, so every `console.log` in the app is a billed log
 * record; `log`/`info`/`debug` are developer chatter and never the thing an
 * on-call engineer searches for. `warn` and `error` are.
 */
type CapturedConsole = Pick<Console, 'warn' | 'error'>

let originalConsole: CapturedConsole | null = null

export interface OtelLoggerConfig {
  serviceName: string
  signozIngestUrl: string
  signozIngestionKey: string
}

/**
 * Initialize OpenTelemetry browser logging
 *
 * This captures console.warn/error and sends them to SigNoz.
 * Also captures unhandled errors and promise rejections.
 */
export function initBrowserLogging(config: OtelLoggerConfig): LoggerProvider {
  if (loggerProvider) {
    return loggerProvider
  }

  const attributes = buildOtelAttributesClient()

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: config.serviceName,
    ...attributes,
  })

  const logExporter = new OTLPLogExporter({
    url: `${config.signozIngestUrl}/v1/logs`,
    headers: {
      'signoz-ingestion-key': config.signozIngestionKey,
    },
  })

  loggerProvider = new LoggerProvider({
    resource,
    processors: [
      new BatchLogRecordProcessor(logExporter, {
        maxQueueSize: 100, // Reduced from 2048 for browser memory
        maxExportBatchSize: 10, // Smaller batches for faster export
        scheduledDelayMillis: 2000, // Export every 2 seconds (vs default 5s)
        exportTimeoutMillis: 10000, // 10 second timeout (vs default 30s)
      }),
    ],
  })

  // Register globally so logs.getLogger() works
  logs.setGlobalLoggerProvider(loggerProvider)

  logger = loggerProvider.getLogger('browser', '1.0.0')

  return loggerProvider
}

/**
 * Intercept console methods to capture logs
 *
 * This wraps console.warn/error to send logs to SigNoz
 * while preserving original console output.
 */
export function interceptConsole(): void {
  if (!logger || originalConsole) {
    return
  }

  // Save the original methods for the two levels we forward.
  originalConsole = {
    warn: console.warn.bind(console),
    error: console.error.bind(console),
  }

  // `log`, `info` and `debug` are deliberately left alone — they still print
  // in the browser, they are just not shipped to SigNoz. `setupErrorHandlers`
  // below covers what console interception would otherwise catch: uncaught
  // exceptions and unhandled rejections.
  console.warn = (...args: unknown[]) => {
    emitLog(SeverityNumber.WARN, 'WARN', args)
    originalConsole!.warn(...args)
  }

  console.error = (...args: unknown[]) => {
    emitLog(SeverityNumber.ERROR, 'ERROR', args)
    originalConsole!.error(...args)
  }
}

/**
 * Restore original console methods
 *
 * This undoes interceptConsole() to stop capturing logs.
 * Called when consent is revoked to ensure no new telemetry is collected.
 */
export function restoreConsole(): void {
  if (!originalConsole) {
    return
  }

  // Restore the two methods `interceptConsole` replaced. The others were never
  // touched, so reassigning them would clobber whatever else wraps them.
  console.warn = originalConsole.warn
  console.error = originalConsole.error

  originalConsole = null
}

/**
 * Set up global error handlers
 *
 * Captures unhandled errors and promise rejections.
 * Returns a cleanup function to remove the handlers.
 */
export function setupErrorHandlers(): (() => void) | undefined {
  if (typeof window === 'undefined') return undefined

  // Handler for unhandled errors
  const errorHandler = (event: ErrorEvent) => {
    if (!logger) return

    emitRedacted(logger, {
      severityNumber: SeverityNumber.ERROR,
      severityText: 'ERROR',
      body: event.message || 'Unknown error',
      attributes: {
        'exception.type': 'UncaughtError',
        'exception.message': event.message || 'Unknown error',
        'exception.stacktrace': event.error?.stack || '',
        'source.filename': event.filename || '',
        'source.lineno': event.lineno || 0,
        'source.colno': event.colno || 0,
        'log.type': 'window.onerror',
      },
    })
  }

  // Handler for unhandled promise rejections
  const rejectionHandler = (event: PromiseRejectionEvent) => {
    if (!logger) return

    const reason = event.reason
    const message = reason instanceof Error ? reason.message : String(reason)
    const stack = reason instanceof Error ? reason.stack : ''

    emitRedacted(logger, {
      severityNumber: SeverityNumber.ERROR,
      severityText: 'ERROR',
      body: message,
      attributes: {
        'exception.type': 'UnhandledRejection',
        'exception.message': message,
        'exception.stacktrace': stack || '',
        'log.type': 'unhandledrejection',
      },
    })
  }

  // Register handlers
  window.addEventListener('error', errorHandler)
  window.addEventListener('unhandledrejection', rejectionHandler)

  // Return cleanup function
  return () => {
    window.removeEventListener('error', errorHandler)
    window.removeEventListener('unhandledrejection', rejectionHandler)
  }
}

/**
 * The one way a record leaves this module: the body and every string
 * attribute have any presigned URL cut to its host first (the same rule the
 * span exporter applies). Console arguments, error messages, stacks and
 * caller context are all forwarded as written, so a library logging its
 * signed stream URL at WARN, or an error quoting a signed GET, would
 * otherwise ship a live credential to SigNoz.
 */
function emitRedacted(target: Logger, record: LogRecord): void {
  target.emit({
    ...record,
    body: typeof record.body === 'string' ? redactPresignedUrls(record.body) : record.body,
    attributes:
      record.attributes &&
      Object.fromEntries(
        Object.entries(record.attributes).map(([key, value]) => [
          key,
          typeof value === 'string' ? redactPresignedUrls(value) : value,
        ])
      ),
  })
}

/**
 * Emit a log record
 */
function emitLog(severityNumber: SeverityNumber, severityText: string, args: unknown[]): void {
  if (!logger) return

  const body = args
    .map((arg) => {
      if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg)
        } catch {
          return String(arg)
        }
      }
      return String(arg)
    })
    .join(' ')

  emitRedacted(logger, {
    severityNumber,
    severityText,
    body,
    attributes: {
      'log.type': `console.${severityText.toLowerCase()}`,
    },
  })
}

/**
 * Get the logger for manual log emission
 */
export function getLogger(): Logger | null {
  return logger
}

/**
 * Get the logger provider (for flushing on page unload)
 */
export function getLoggerProvider(): LoggerProvider | null {
  return loggerProvider
}

/**
 * Manually record an exception (equivalent to HyperDX.recordException)
 */
export function recordException(error: Error, context?: Record<string, unknown>): void {
  if (!logger) {
    console.error('Cannot record exception: logger not initialized', error)
    return
  }

  emitRedacted(logger, {
    severityNumber: SeverityNumber.ERROR,
    severityText: 'ERROR',
    body: error.message,
    attributes: {
      'exception.type': error.name,
      'exception.message': error.message,
      'exception.stacktrace': error.stack || '',
      'log.type': 'recordException',
      // Absent values drop rather than stringify — String(undefined) is the
      // literal "undefined" in SigNoz, which reads as a real value. null
      // still passes through as "null": callers coalesce to null on purpose
      // (browser-logger.ts has the same contract).
      ...Object.fromEntries(
        Object.entries(context || {})
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => [k, String(v)])
      ),
    },
  })
}

/**
 * Manually log an action (equivalent to HyperDX.addAction)
 */
export function addAction(name: string, attributes?: Record<string, unknown>): void {
  if (!logger) {
    console.warn('Cannot add action: logger not initialized', name)
    return
  }

  emitRedacted(logger, {
    severityNumber: SeverityNumber.INFO,
    severityText: 'INFO',
    body: name,
    attributes: {
      'log.type': 'action',
      // Absent values drop rather than stringify — String(undefined) is the
      // literal "undefined" in SigNoz, which reads as a real value. null
      // still passes through as "null": callers coalesce to null on purpose
      // (browser-logger.ts has the same contract).
      ...Object.fromEntries(
        Object.entries(attributes || {})
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => [k, String(v)])
      ),
    },
  })
}

/**
 * Shutdown logging (for cleanup)
 */
export async function shutdownLogging(): Promise<void> {
  if (loggerProvider) {
    await loggerProvider.shutdown()
    loggerProvider = null
    logger = null
  }
}
